import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Check, X, Clock, ArrowUpCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  getWithdrawals,
  updateWithdrawalStatus,
  WithdrawalData,
  formatCurrency,
  formatDate,
} from '@/features/admin/services/adminService'

export default function AdminWithdrawals() {
  const { t } = useTranslation()
  const [withdrawals, setWithdrawals] = useState<WithdrawalData[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [filter, setFilter] = useState<string>('pending')
  const [processingId, setProcessingId] = useState<string | null>(null)

  useEffect(() => {
    loadWithdrawals()
  }, [filter])

  const loadWithdrawals = async () => {
    setIsLoading(true)
    const data = await getWithdrawals(filter === 'all' ? undefined : filter)
    setWithdrawals(data)
    setIsLoading(false)
  }

  const handleApprove = async (withdrawal: WithdrawalData) => {
    setProcessingId(withdrawal.id)
    await updateWithdrawalStatus(withdrawal.id, 'approved', withdrawal.userId, withdrawal.amount)
    loadWithdrawals()
    setProcessingId(null)
  }

  const handleReject = async (withdrawal: WithdrawalData) => {
    if (!confirm('¿Rechazar este retiro? El monto se devolverá al balance del usuario.')) return
    setProcessingId(withdrawal.id)
    await updateWithdrawalStatus(withdrawal.id, 'rejected', withdrawal.userId, withdrawal.amount)
    loadWithdrawals()
    setProcessingId(null)
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <span className="text-xs bg-yellow-900/50 text-yellow-400 px-2 py-1 rounded flex items-center gap-1"><Clock className="w-3 h-3" /> Pendiente</span>
      case 'approved':
        return <span className="text-xs bg-green-900/50 text-green-400 px-2 py-1 rounded flex items-center gap-1"><Check className="w-3 h-3" /> Aprobado</span>
      case 'rejected':
        return <span className="text-xs bg-red-900/50 text-red-400 px-2 py-1 rounded flex items-center gap-1"><X className="w-3 h-3" /> Rechazado</span>
      default:
        return null
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-cinzel text-2xl text-casino-gold">{t('admin.withdrawals')}</h1>
      </div>

      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList>
          <TabsTrigger value="pending">Pendientes</TabsTrigger>
          <TabsTrigger value="approved">Aprobados</TabsTrigger>
          <TabsTrigger value="rejected">Rechazados</TabsTrigger>
          <TabsTrigger value="all">Todos</TabsTrigger>
        </TabsList>

        <TabsContent value={filter} className="mt-4">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-casino-gold" />
            </div>
          ) : withdrawals.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <ArrowUpCircle className="w-12 h-12 mx-auto mb-4 text-gray-600" />
                <p className="text-gray-400">No hay retiros {filter === 'pending' ? 'pendientes' : ''}</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {withdrawals.map((withdrawal) => (
                <Card key={withdrawal.id} className={withdrawal.status === 'pending' ? 'border-yellow-500/30' : ''}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <p className="font-medium">{withdrawal.userName || 'Usuario'}</p>
                          {getStatusBadge(withdrawal.status)}
                        </div>
                        <p className="text-sm text-gray-400">{withdrawal.userEmail}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {withdrawal.method}
                          {withdrawal.accountInfo && (
                            <span className="ml-2 text-casino-gold">
                              Cuenta: {withdrawal.accountInfo}
                            </span>
                          )}
                        </p>
                      </div>

                      <div className="text-right mr-4">
                        <p className="text-xl font-bold text-red-400">
                          -{formatCurrency(withdrawal.amount)}
                        </p>
                        <p className="text-xs text-gray-500">{formatDate(withdrawal.createdAt)}</p>
                      </div>

                      {withdrawal.status === 'pending' && (
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleApprove(withdrawal)}
                            disabled={processingId === withdrawal.id}
                            className="border-green-600 text-green-500 hover:bg-green-900/20"
                          >
                            {processingId === withdrawal.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Check className="w-4 h-4" />
                            )}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleReject(withdrawal)}
                            disabled={processingId === withdrawal.id}
                            className="border-red-600 text-red-500 hover:bg-red-900/20"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}
